/** Public inquiry status check (route: /inquiry/status/:token). Anonymous, token-gated;
 *  shows only the inquiry's own status — no internal notes, assignee, or other PII.
 *
 *  It is also where a signed-in customer's enquiry notifications open ("Enquiry received", "We are
 *  on it", "Enquiry closed"), so it has to answer "where does my enquiry stand?" on its own:
 *   - the steps are the real lifecycle — received, picked up, contacted, CLOSED. It used to end in
 *     "Converted", a step retired in September 2026 that no enquiry can reach any more, and a closed
 *     enquiry collapsed to one grey line with nothing about how it ended;
 *   - it opens on the site's dark page band. Without it the transparent header's white links sat on
 *     the light page and could not be read, and the card slid up under the header. */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageSearch, CheckCircle2, Phone } from 'lucide-react';
import { getInquiryStatus } from './inquiryApi';
import { cn } from '../ui';
import { Seo } from '../lib/seo';
import { PageHeader } from './components/common/PageHeader';
import { company } from './data/company';
import { formatIstDate } from '../lib/time';

type Step = { key: string; label: string; at?: string | null; done: boolean; current: boolean };

// The app's own IST date format ("26 Sep 2026"). `toLocaleDateString('en-IN')` prints "Sept" from ICU.
const fmt = (iso?: string | null) => (iso ? formatIstDate(iso) : null);

export function InquiryStatusPage() {
  const { token = '' } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inquiry-status', token],
    queryFn: () => getInquiryStatus(token),
    enabled: !!token,
    retry: false,
  });

  const shell = (body: React.ReactNode, title = 'Enquiry status', subtitle?: string) => (
    <>
      <Seo noindex title="Inquiry status" description="Track the status of your product inquiry with Green Bay Biotech." />
      {/* Keyed: SplitHeading splits the text it MOUNTS with, so without a remount the loading title stayed. */}
      <PageHeader key={title} eyebrow="Track your enquiry" title={title} subtitle={subtitle} />
      <div className="container-x py-10 md:py-14">
        <div className="mx-auto max-w-2xl rounded-card border border-line bg-surface p-6 sm:p-7">{body}</div>
      </div>
    </>
  );

  if (isLoading) {
    return shell(<div className="grid place-items-center py-10 text-muted"><Loader2 className="size-7 animate-spin" /></div>);
  }
  if (isError || !data) {
    return shell(
      <div className="text-center">
        <span className="mx-auto grid size-14 place-items-center rounded-full bg-surface-2 text-muted"><PackageSearch className="size-7" /></span>
        <p className="mt-4 text-lg font-bold text-content">Enquiry not found</p>
        <p className="mt-1 text-sm text-muted">This tracking link looks invalid or expired.</p>
        <Link to="/catalog" className="mt-4 inline-block text-sm font-bold text-brand-700! hover:underline">Browse products</Link>
      </div>,
    );
  }

  const status = data.status;
  // CONVERTED is a retired end state; an old enquiry that reached it reads as finished, like a closed one.
  const finished = status === 'CLOSED' || status === 'CONVERTED';
  const picked = !!data.assigned || status !== 'NEW';
  const contacted = !!data.contactedAt || status === 'CONTACTED';
  const steps: Step[] = [
    { key: 'received', label: 'Received', at: data.createdAt, done: true, current: false },
    { key: 'assigned', label: 'Picked up by our team', done: picked, current: false },
    { key: 'contacted', label: 'Contacted you', at: data.contactedAt, done: contacted, current: false },
    { key: 'closed', label: 'Closed', at: data.closedAt, done: finished, current: false },
  ];
  // The current step is the last one reached — unless the enquiry is finished, when nothing is "current".
  if (!finished) {
    const last = [...steps].reverse().find((s) => s.done);
    if (last) last.current = true;
  }

  const subtitle = [data.productName ? `For ${data.productName}` : null, `Raised by ${data.customerName}`, fmt(data.createdAt)]
    .filter(Boolean).join(' · ');

  return shell(
    <>
      <ol className="space-y-4">
        {steps.map((s, i) => (
          <li key={s.key} className="flex items-center gap-3">
            <span className={cn('grid size-7 shrink-0 place-items-center rounded-full border text-[12px] font-bold',
              s.done ? 'border-brand-600 bg-brand-600 text-white' : 'border-line text-muted')}>
              {s.done ? <CheckCircle2 className="size-4" /> : i + 1}
            </span>
            <span className={cn('min-w-0 text-[15px]', s.current ? 'font-bold text-content' : s.done ? 'text-content' : 'text-muted')}>
              {s.label}
              {s.done && fmt(s.at) && <span className="ml-2 text-[13px] font-normal text-muted">{fmt(s.at)}</span>}
            </span>
            {s.current && <span className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">Now</span>}
          </li>
        ))}
      </ol>

      {finished ? (
        <div className="mt-6 rounded-[12px] border border-line bg-surface-2/60 p-4 text-sm text-content">
          This enquiry is closed{data.outcome ? <> — {data.outcome}</> : '.'}
          <span className="mt-1 block text-muted">Still need help? Send a new enquiry, or call us.</span>
        </div>
      ) : (
        <p className="mt-6 text-sm text-muted">
          {contacted
            ? 'Our team has been in touch. If you are still waiting on something, call us.'
            : picked
              ? 'A member of our team has it and will call you.'
              : 'We have your enquiry and will pass it to the team for your area.'}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-line pt-5">
        <Link to="/catalog" className="text-sm font-bold text-brand-700! hover:underline">← Browse more products</Link>
        <a href={`tel:${company.phoneHref}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-700! hover:underline">
          <Phone className="size-4" /> Call us
        </a>
      </div>
    </>,
    data.inquiryNo,
    subtitle,
  );
}
